import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { FileText } from "lucide-react";
import { zar } from "@/api";

const TERMS = {
  48: { ownership: 2916.67, total: 5886.67 },
  60: { ownership: 2333.33, total: 5303.33 },
};
const RENTAL_MONTH = 2970;

export default function PricingExample() {
  const [term, setTerm] = useState(48);
  const t = TERMS[term];

  const rows = [
    ["Cash vehicle price", zar(150000)],
    ["Ownership deposit", zar(10000)],
    ["Ownership balance", zar(140000)],
    [`Ownership allocation / month (${term} m)`, zar(t.ownership)],
    ["Rental & service / month (R99/day × 30)", zar(RENTAL_MONTH)],
  ];

  return (
    <section data-testid="pricing-example-section" className="py-24 md:py-32">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">Radical transparency</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-black text-white mb-6">
            See exactly where every rand goes.
          </h2>
          <p className="text-base text-slate-400 leading-relaxed mb-6">
            Before you sign, you see the vehicle-specific price, the separated
            rental &amp; service charge, and the exact ownership allocation of
            every payment. Your monthly statement then proves the balance
            shrinking — all the way to the title.
          </p>
          <p className="text-base text-slate-400 leading-relaxed">
            No undisclosed balloon. A clear early-settlement figure at any
            point. That's the CompCar promise.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="bg-[#122659] border border-sky-300/20 rounded-xl overflow-hidden shadow-2xl" data-testid="pricing-breakdown-card">
            <div className="flex items-center justify-between px-8 py-5 border-b border-sky-300/10">
              <span className="flex items-center gap-2 text-sm font-bold text-white">
                <FileText size={16} className="text-[#F57C00]" />
                Illustrative example
              </span>
              <div className="flex gap-2" data-testid="term-toggle">
                {[48, 60].map((m) => (
                  <button
                    key={m}
                    data-testid={`term-toggle-${m}`}
                    onClick={() => setTerm(m)}
                    className={`text-xs font-bold px-4 py-2 rounded-md transition-colors duration-200 ${
                      term === m ? "bg-[#F57C00] text-white" : "bg-[#0B1F4E] text-slate-400 hover:text-white"
                    }`}
                  >
                    {m} months
                  </button>
                ))}
              </div>
            </div>
            <div className="px-8 py-6 space-y-4">
              {rows.map(([label, value]) => (
                <div key={label} className="flex justify-between items-center text-sm border-b border-white/5 pb-3">
                  <span className="text-slate-400">{label}</span>
                  <span className="font-semibold text-white">{value}</span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2">
                <span className="text-sm font-bold text-sky-300 uppercase tracking-wider">Total monthly</span>
                <span className="font-display text-3xl font-black text-[#F57C00]" data-testid="pricing-total">
                  {zar(t.total)}
                </span>
              </div>
            </div>
            <p className="px-8 py-4 text-[11px] text-slate-500 leading-relaxed border-t border-sky-300/10 bg-[#0B1F4E]/50">
              Excludes insurance, tracking and motor-plan amounts. Entry pricing
              from R99/day. Final quotation is vehicle-specific and provided in
              writing before signature, with a full pre-agreement statement
              where the National Credit Act applies.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
